import { useQueryClient } from '@tanstack/react-query';
import { useAxiosInfiniteQuery, useAxiosMutation, useAxiosQuery } from './use-axios.hook';
import { UpdateArcgisCivil3dParcelSettingApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/parcels/update-arcgis-civil3d-parcel-setting.request';
import { CreateArcgisCivil3dParcelSettingApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/parcels/create-arcgis-civil3d-parcel-setting.request';
import {
  updateParcelSetting,
  createParcelSetting,
  createPropertyMappingForParcel,
  getParcelSetting,
  deleteParcelSettings,
  deletePropertyMappingFromParcel,
  getAllPropertyMappingsForParcel,
  updatePropertyMappingsForParcel,
  duplicateParcelSettings,
  createPropertyMappingsForParcels,
  searchPropertyMappingsForParcels,
} from '../../api/arcgis-civil3d-parcel-setting.api';
import { ArcgisCivil3dParcelSettingApiModel } from '../../../../types/models/arcgis-civil3d-parcel-setting.model';
import { GetArcgisCivil3dParcelSettingApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/parcels/get-arcgis-civil3d-parcel-setting.request';
import { DeleteArcgisCivil3dParcelSettingsApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/parcels/delete-arcgis-civil3d-parcel-settings.request';
import { CreatePropertyMappingForParcelApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/parcels/create-property-mapping-for-parcel.request';
import { DeletePropertyMappingForParcelApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/parcels/delete-property-mapping-for-parcel.request';
import { GetAllPropertyMappingsForParcelApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/parcels/get-all-property-mappings-for-parcel.request';
import { UpdatePropertyMappingsForParcelApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/parcels/update-property-mappings-for-parcel.request';
import { DuplicateArcgisCivil3dParcelsApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/parcels/duplicate-arcgis-civil3d-parcel-settings.request';
import { CreatePropertyMappingsForParcelsApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/parcels/create-property-mappings-for-parcels.request';
import { SearchPropertyMappingsForParcelsApiRequest } from '../../../../types/requests/arcgis-civil3d-settings/parcels/search-property-mappings-for-parcels.request';

const baseQueryKey = 'parcel-setting';
const searchQueryKey = 'search';
const propertyMappingQueryKey = 'property-mappings';

export const useUpdateParcelSetting = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: UpdateArcgisCivil3dParcelSettingApiRequest) => (await updateParcelSetting(params)).data,
    onSuccess: (parcel) =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey[0] === baseQueryKey &&
          (queryKey[1] === 'search' ||
            (queryKey[1] as UpdateArcgisCivil3dParcelSettingApiRequest)?.configurationId === parcel.configuration?.id),
      }),
  });
  return [request, { ...result, parcel: data }] as const;
};

export const useCreateParcelSetting = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: CreateArcgisCivil3dParcelSettingApiRequest) => (await createParcelSetting(params)).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [baseQueryKey, searchQueryKey] }),
  });
  return [request, { ...result, parcel: data }] as const;
};

export const useGetParcelSetting = <T extends ArcgisCivil3dParcelSettingApiModel>(
  params: GetArcgisCivil3dParcelSettingApiRequest,
) => {
  const { data, ...result } = useAxiosQuery<T>({
    queryKey: [baseQueryKey, params],
    queryFn: async () => (await getParcelSetting<T>(params)).data,
    enabled: Boolean(params.parcelId && !Number.isNaN(params.parcelId)),
  });
  return { ...result, parcel: data } as const;
};

export const useDuplicateParcelSettings = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: DuplicateArcgisCivil3dParcelsApiRequest) => (await duplicateParcelSettings(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === searchQueryKey,
      }),
  });
  return [request, result] as const;
};

export const useDeleteParcelSettings = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: DeleteArcgisCivil3dParcelSettingsApiRequest) =>
      (await deleteParcelSettings(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === searchQueryKey,
      }),
  });
  return [request, result] as const;
};

export const useCreatePropertyMappingForParcel = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: CreatePropertyMappingForParcelApiRequest) =>
      (await createPropertyMappingForParcel(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === propertyMappingQueryKey,
      }),
  });
  return [request, { ...result, ...data }] as const;
};

export const useCreatePropertyMappingsForParcels = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: CreatePropertyMappingsForParcelsApiRequest) =>
      (await createPropertyMappingsForParcels(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === searchQueryKey,
      }),
  });
  return [request, { ...result, ...data }] as const;
};

export const useSearchPropertyMappingsForParcels = (params: SearchPropertyMappingsForParcelsApiRequest) => {
  const { data, ...result } = useAxiosInfiniteQuery({
    queryKey: [baseQueryKey, searchQueryKey, params],
    queryFn: async ({ pageParam }) => (await searchPropertyMappingsForParcels({ cursor: pageParam, ...params })).data,
    initialPageParam: params.cursor,
    getNextPageParam: (lastResponse) => lastResponse.nextCursor,
  });
  return { ...result, response: data } as const;
};

export const useUpdatePropertyMappingsForParcel = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: UpdatePropertyMappingsForParcelApiRequest) =>
      (await updatePropertyMappingsForParcel(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === propertyMappingQueryKey,
      }),
  });
  return [request, { ...result, ...data }] as const;
};

export const useGetAllPropertyMappingsForParcel = (params: GetAllPropertyMappingsForParcelApiRequest) => {
  const { data, ...result } = useAxiosQuery({
    queryKey: [baseQueryKey, params],
    queryFn: async () => (await getAllPropertyMappingsForParcel(params)).data,
    enabled: Boolean(params.parcelId && !Number.isNaN(params.parcelId)),
  });
  return { ...result, ...data } as const;
};

export const useDeletePropertyMappingFromParcel = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: DeletePropertyMappingForParcelApiRequest) =>
      (await deletePropertyMappingFromParcel(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === propertyMappingQueryKey,
      }),
  });
  return [request, result] as const;
};
